/**
 * GLOWSERA QUIZ - API SHOPIFY & MOTEUR DE RECOMMANDATION
 * Interface avec Shopify Storefront API et algorithme de scoring produits
 * Compatible Shopify Cornerstone 4.2.2
 */

/**
 * Configuration API Shopify Storefront
 */
const SHOPIFY_CONFIG = {
  // Ces valeurs doivent etre configurees dans les meta tags de theme.liquid
  get storefrontToken() {
    const meta = document.querySelector('meta[name="shopify-storefront-token"]');
    return meta ? meta.content : null;
  },
  get shopDomain() {
    // Utiliser le domaine Shopify depuis window.Shopify
    return window.Shopify ? window.Shopify.shop : null;
  },
  apiVersion: '2026-01'
};

/**
 * Cache des produits pour eviter requetes repetees
 */
const ProductCache = {
  data: {},
  ttl: 10 * 60 * 1000, // 10 minutes

  set(key, value) {
    this.data[key] = {
      value: value,
      timestamp: Date.now()
    };
  },

  get(key) {
    const cached = this.data[key];
    if (!cached) return null;

    // Verifier si expire
    if (Date.now() - cached.timestamp > this.ttl) {
      delete this.data[key];
      return null;
    }

    return cached.value;
  },

  clear() {
    this.data = {};
  }
};

/**
 * Recuperer les produits Shopify par tags via Storefront API
 * @param {Array<string>} tags - Tags a rechercher
 * @param {string} quizType - Type de quiz (pour collection filter)
 * @param {number} limit - Nombre max de produits (default 50)
 * @returns {Promise<Array>} Liste de produits
 */
async function fetchProductsByTags(tags, quizType, limit = 250) {
  try {
    console.log('[Glowsera Quiz API] Fetch produits pour tags:', tags);

    // Verifier la config
    if (!SHOPIFY_CONFIG.storefrontToken || !SHOPIFY_CONFIG.shopDomain) {
      throw new Error('Configuration Shopify Storefront API manquante');
    }

    // Verifier le cache
    const cacheKey = `${quizType}_${tags.join(',')}_${limit}`;
    const cached = ProductCache.get(cacheKey);
    if (cached) {
      console.log('[Glowsera Quiz API] Produits recuperes du cache');
      return cached;
    }

    // Construire la query GraphQL
    // On va chercher les produits qui contiennent au moins un des tags
    const tagQuery = tags.map(tag => `tag:${tag}`).join(' OR ');

    const query = `
      query getProductsByTags($query: String!, $first: Int!) {
        products(first: $first, query: $query) {
          edges {
            node {
              id
              title
              handle
              description
              tags
              productType
              vendor
              availableForSale
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              images(first: 2) {
                edges {
                  node {
                    url
                    altText
                    width
                    height
                  }
                }
              }
              compareAtPriceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 5) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    priceV2 {
                      amount
                      currencyCode
                    }
                    compareAtPriceV2 {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      query: tagQuery,
      first: limit
    };

    // Effectuer la requete
    const response = await fetch(`https://${SHOPIFY_CONFIG.shopDomain}/api/${SHOPIFY_CONFIG.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontToken
      },
      body: JSON.stringify({
        query: query,
        variables: variables
      })
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      console.error('[Glowsera Quiz API] Erreurs GraphQL:', data.errors);
      throw new Error('Erreur GraphQL');
    }

    // Transformer les donnees
    const products = data.data.products.edges.map(edge => {
      const node = edge.node;

      const compareAtAmount = node.compareAtPriceRange?.minVariantPrice?.amount;
      const compareAtPrice = compareAtAmount ? parseFloat(compareAtAmount) : null;
      const price = parseFloat(node.priceRange.minVariantPrice.amount);

      return {
        id: node.id,
        title: node.title,
        handle: node.handle,
        description: node.description,
        tags: node.tags,
        productType: node.productType,
        vendor: node.vendor,
        availableForSale: node.availableForSale,
        price: price,
        compareAtPrice: compareAtPrice && compareAtPrice > price ? compareAtPrice : null,
        currency: node.priceRange.minVariantPrice.currencyCode,
        images: node.images.edges.map(img => ({
          url: img.node.url,
          alt: img.node.altText,
          width: img.node.width,
          height: img.node.height
        })),
        variants: node.variants.edges.map(v => ({
          id: v.node.id,
          title: v.node.title,
          availableForSale: v.node.availableForSale,
          price: parseFloat(v.node.priceV2.amount),
          compareAtPrice: v.node.compareAtPriceV2 ? parseFloat(v.node.compareAtPriceV2.amount) : null,
          currency: v.node.priceV2.currencyCode
        }))
      };
    });

    console.log('[Glowsera Quiz API] Produits recuperes:', products.length);

    // Mettre en cache
    ProductCache.set(cacheKey, products);

    return products;

  } catch (error) {
    console.error('[Glowsera Quiz API] Erreur fetch produits:', error);
    throw error;
  }
}

/**
 * Ajouter un produit au panier Shopify
 * @param {string} variantId - ID de la variante Shopify
 * @param {number} quantity - Quantite (default 1)
 * @returns {Promise<Object>} Reponse panier
 */
async function addToCart(variantId, quantity = 1) {
  try {
    console.log('[Glowsera Quiz API] Ajout au panier:', { variantId, quantity });

    // Extraire l'ID numerique du GID Shopify
    const numericId = variantId.split('/').pop();

    // Utiliser l'API Cart Ajax de Shopify
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: numericId,
          quantity: quantity
        }]
      })
    });

    if (!response.ok) {
      throw new Error('Erreur ajout au panier');
    }

    const cartData = await response.json();
    console.log('[Glowsera Quiz API] Produit ajoute au panier:', cartData);

    // Declencher l'event Shopify pour update UI
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: cartData
    }));

    return cartData;

  } catch (error) {
    console.error('[Glowsera Quiz API] Erreur ajout panier:', error);
    throw error;
  }
}

/**
 * MOTEUR DE RECOMMANDATION GLOWSERA
 * Algorithme de scoring et selection des meilleurs produits
 */
class GlowseraRecommendationEngine {
  /**
   * Constructeur
   * @param {string} quizType - Type de quiz
   * @param {Object} config - Configuration du quiz
   */
  constructor(quizType, config) {
    this.quizType = quizType;
    this.config = config;
    this.recommendations = config.recommendations || {};

    console.log('[Glowsera Recommendation Engine] Initialisation pour quiz:', quizType);
  }

  /**
   * Obtenir les recommandations pour un profil utilisateur
   * @param {Object} userProfile - Profil utilisateur genere
   * @returns {Promise<Array>} Produits recommandes avec scores
   */
  async getRecommendations(userProfile) {
    try {
      console.log('[Glowsera Recommendation Engine] Generation recommandations pour profil:', userProfile);

      // Determiner les tags a rechercher
      const searchTags = this.determineSearchTags(userProfile);
      console.log('[Glowsera Recommendation Engine] Tags de recherche:', searchTags);

      // Fetch les produits
      const products = await fetchProductsByTags(searchTags, this.quizType);

      // Filtrer les produits par domaine (Peau, Cheveux, etc.) pour eviter les mélanges
      const inScopeProducts = this.filterByScope(products);
      console.log(`[Glowsera Recommendation Engine] Produits dans le domaine ${this.quizType}:`, inScopeProducts.length);

      // Filtrer les produits disponibles
      const availableProducts = inScopeProducts.filter(p => p.availableForSale);
      console.log('[Glowsera Recommendation Engine] Produits disponibles:', availableProducts.length);

      // Calculer le score pour chaque produit
      const scoredProducts = availableProducts.map(product => ({
        ...product,
        score: this.calculateScore(product, searchTags),
        matchReasons: this.getMatchReasons(product, userProfile)
      }));

      // Trier par score descendant
      scoredProducts.sort((a, b) => b.score - a.score);

      // Selectionner les meilleurs produits avec diversification
      const selectedProducts = this.selectDiversifiedProducts(scoredProducts, userProfile);

      return selectedProducts;

    } catch (error) {
      console.error('[Glowsera Recommendation Engine] Erreur generation recommandations:', error);
      throw error;
    }
  }

  /**
   * Determiner les tags a utiliser pour la recherche
   * @param {Object} userProfile - Profil utilisateur
   * @returns {Array<string>} Liste de tags
   */
  determineSearchTags(userProfile) {
    const tags = [];

    // Ajouter le scope tag pour filtrer au niveau API
    const scopeTags = {
      skin: 'categorie-visage',
      hair: 'categorie-cheveux',
      makeup: 'categorie-maquillage',
      body: 'categorie-corps'
    };
    if (scopeTags[this.quizType]) {
      tags.push(scopeTags[this.quizType]);
    }

    // Ajouter tous les tags du profil
    if (userProfile.tags && userProfile.tags.length > 0) {
      tags.push(...userProfile.tags);
    }

    // Ajouter les categories obligatoires si definies
    if (this.recommendations.mustInclude) {
      tags.push(...this.recommendations.mustInclude);
    }

    // Dedupliquer
    return [...new Set(tags)];
  }

  /**
   * Calculer le score de pertinence d'un produit
   * @param {Object} product - Produit Shopify
   * @param {Array<string>} searchTags - Tags de recherche (profil utilisateur)
   * @returns {number} Score final
   */
  calculateScore(product, searchTags) {
    let score = 0;
    const pTags = new Set((product.tags || []).map(t => t.toLowerCase()));
    const sTags = searchTags.map(t => t.toLowerCase());

    // 1. Definition des poids par categorie de tags
    const weights = {
      type: 50,      // Tags de base : peau-seche, cheveux-crepu, etc.
      concern: 30,   // Tags de besoin : soin-anti-age, soin-hydratation, etc.
      step: 20,      // Tags de routine : nettoyant, serum, etc.
      bonus: 15      // Tags de preferences : vegan, naturel
    };

    // 2. Mapping des conflits (Scoring Negatif)
    // Si l'utilisateur est X, on penalise les produits marqués Y
    const conflicts = {
      'peau-seche': ['peau-grasse', 'fini-mat'],
      'peau-grasse': ['peau-seche', 'texture-riche'],
      'peau-mixte': [],
      'peau-sensible': ['exfoliant-fort'],
      'cheveux-fins': ['texture-lourde', 'beurre-karite']
    };

    // 3. Calcul par correspondance
    sTags.forEach(sTag => {
      if (pTags.has(sTag)) {
        // Determiner le poids selon le prefixe du tag
        if (sTag.startsWith('peau-') || sTag.startsWith('cheveux-')) {
          score += weights.type;
        } else if (sTag.startsWith('soin-')) {
          score += weights.concern;
        } else if (['nettoyant', 'serum', 'hydratant', 'lotion-tonique', 'masque'].includes(sTag)) {
          score += weights.step;
        } else {
          score += 10; // Autres tags
        }
      }
    });

    // 4. Application des malus (Conflicts)
    sTags.forEach(sTag => {
      if (conflicts[sTag]) {
        conflicts[sTag].forEach(forbiddenTag => {
          if (pTags.has(forbiddenTag)) {
            score -= 60; // Grosse penalite pour eviter le mauvais produit
          }
        });
      }
    });

    // 5. Bonus specifiques (Ethique/Qualite)
    if (pTags.has('vegan')) score += weights.bonus;
    if (pTags.has('naturel') || pTags.has('bio')) score += weights.bonus;

    return Math.max(0, score); // Jamais de score negatif
  }

  /**
   * Obtenir les raisons du match pour affichage utilisateur
   * @param {Object} product - Produit
   * @param {Object} userProfile - Profil utilisateur
   * @returns {Array<string>} Raisons du match
   */
  getMatchReasons(product, userProfile) {
    const reasons = [];

    // Match type de peau/cheveux
    const skinTypes = ['peau-normale', 'peau-seche', 'peau-grasse', 'peau-mixte', 'peau-sensible'];
    const hairTypes = ['cheveux-lisses', 'cheveux-ondules', 'cheveux-boucles', 'cheveux-crepu'];

    userProfile.tags.forEach(tag => {
      if (product.tags.includes(tag)) {
        if (skinTypes.includes(tag)) {
          reasons.push('Adapte a votre type de peau');
        } else if (hairTypes.includes(tag)) {
          reasons.push('Parfait pour votre type de cheveux');
        }
      }
    });

    // Match preoccupations
    if (userProfile.concerns && userProfile.concerns.length > 0) {
      const concernMatches = userProfile.concerns.filter(c => product.tags.includes(c));
      if (concernMatches.length > 0) {
        reasons.push('Répond à vos préoccupations');
      }
    }

    return reasons.slice(0, 3);
  }

  /**
   * Filtre les produits pour s'assurer qu'ils appartiennent au domaine du quiz
   * Utilise les scope tags (categorie-visage, categorie-cheveux, etc.) en priorite,
   * avec fallback sur les mots-cles pour les produits pas encore tagges.
   * @param {Array} products - Liste de produits
   * @returns {Array} Produits filtres
   */
  filterByScope(products) {
    // Mapping quiz type -> scope tag
    const scopeTags = {
      skin: 'categorie-visage',
      hair: 'categorie-cheveux',
      makeup: 'categorie-maquillage',
      body: 'categorie-corps'
    };

    // Fallback keywords pour les produits sans scope tag
    const scopeKeywords = {
      skin: ['peau', 'visage', 'teint', 'soin-visage'],
      hair: ['cheveux', 'shampoing', 'apres-shampoing', 'scalp', 'cuir-chevelu', 'bond-building'],
      makeup: ['maquillage', 'levres', 'yeux', 'mascara', 'fond-de-teint', 'blush', 'eyeliner', 'sourcils', 'poudre'],
      body: ['corps', 'mains', 'pieds', 'deodorant', 'gommage-corps', 'lotion-corps']
    };

    const currentScope = this.quizType;
    const currentScopeTag = scopeTags[currentScope];
    const allScopeTags = Object.values(scopeTags);

    return products.filter(product => {
      const tags = (product.tags || []).map(t => t.toLowerCase());

      // 1. Si le produit a le scope tag du quiz courant -> inclure
      if (currentScopeTag && tags.includes(currentScopeTag)) {
        return true;
      }

      // 2. Si le produit a un scope tag d'un AUTRE quiz -> exclure
      const hasOtherScope = allScopeTags.some(st => st !== currentScopeTag && tags.includes(st));
      if (hasOtherScope) {
        return false;
      }

      // 3. Fallback : produit sans aucun scope tag -> detection par mots-cles
      const title = (product.title || '').toLowerCase();
      const type = (product.productType || '').toLowerCase();

      const matchesCurrent = tags.some(t => scopeKeywords[currentScope].some(k => t.includes(k))) ||
        scopeKeywords[currentScope].some(k => title.includes(k)) ||
        scopeKeywords[currentScope].some(k => type.includes(k));

      return matchesCurrent;
    });
  }

  /**
   * Selectionner les produits avec diversification des categories et des vendors
   * @param {Array} scoredProducts - Produits avec scores tries
   * @param {Object} userProfile - Profil utilisateur
   * @returns {Array} Produits selectionnes
   */
  selectDiversifiedProducts(scoredProducts, userProfile) {
    const selected = [];
    const maxProducts = this.recommendations.maxProducts || 4;
    const maxPerVendor = this.recommendations.maxPerVendor || 3;
    const usedCategories = new Set();
    const vendorCount = {};

    // Helper pour compter les vendors
    const canAddVendor = (vendor) => {
      if (!vendor) return true;
      return (vendorCount[vendor] || 0) < maxPerVendor;
    };
    const trackVendor = (vendor) => {
      if (!vendor) return;
      vendorCount[vendor] = (vendorCount[vendor] || 0) + 1;
    };

    // Definir les categories a diversifier selon le type de quiz
    const categoryTags = this.getCategoryTags();

    // ETAPE 1: Ajouter les produits des categories obligatoires
    if (this.recommendations.mustInclude) {
      this.recommendations.mustInclude.forEach(category => {
        const match = scoredProducts.find(p =>
          p.tags.includes(category) && !selected.includes(p) && canAddVendor(p.vendor)
        );

        if (match) {
          selected.push(match);
          trackVendor(match.vendor);

          // Marquer la categorie comme utilisee
          const productCategory = this.getProductCategory(match, categoryTags);
          if (productCategory) {
            usedCategories.add(productCategory);
          }
        }
      });
    }

    // ETAPE 2: Completer avec les meilleurs scores en diversifiant categories ET vendors
    for (const product of scoredProducts) {
      if (selected.length >= maxProducts) break;
      if (selected.includes(product)) continue;
      if (!canAddVendor(product.vendor)) continue;

      // Determiner la categorie du produit
      const productCategory = this.getProductCategory(product, categoryTags);

      // Si pas de categorie ou categorie pas encore utilisee, ajouter
      if (!productCategory || !usedCategories.has(productCategory)) {
        selected.push(product);
        trackVendor(product.vendor);

        if (productCategory) {
          usedCategories.add(productCategory);
        }
      }
    }

    // ETAPE 3: Si pas assez, relacher la contrainte categorie (garder vendor)
    if (selected.length < maxProducts) {
      for (const product of scoredProducts) {
        if (selected.length >= maxProducts) break;
        if (selected.includes(product)) continue;
        if (!canAddVendor(product.vendor)) continue;

        selected.push(product);
        trackVendor(product.vendor);
      }
    }

    // ETAPE 4: Si toujours pas assez, relacher aussi la contrainte vendor
    if (selected.length < maxProducts) {
      for (const product of scoredProducts) {
        if (selected.length >= maxProducts) break;
        if (!selected.includes(product)) {
          selected.push(product);
        }
      }
    }

    return selected.slice(0, maxProducts);
  }

  /**
   * Obtenir les tags de categories selon le type de quiz
   * @returns {Array<string>} Tags de categories
   */
  getCategoryTags() {
    const categories = {
      skin: ['nettoyant', 'serum', 'hydratant', 'masque', 'soin-yeux', 'lotion-tonique', 'exfoliant', 'protection-solaire'],
      hair: ['shampoing', 'apres-shampoing', 'masque-cheveux', 'huile-cheveux', 'coiffant', 'soin-cheveux'],
      makeup: ['fond-de-teint', 'correcteur', 'mascara', 'rouge-a-levres', 'blush', 'fard-a-paupieres', 'eye-liner', 'sourcils', 'poudre', 'base'],
      body: ['gel-douche', 'lotion-corps', 'gommage-corps', 'huile-corps', 'creme-mains', 'creme-pieds', 'deodorant']
    };

    return categories[this.quizType] || [];
  }

  /**
   * Determiner la categorie principale d'un produit (avec fallback sur le titre)
   * @param {Object} product - Produit
   * @param {Array<string>} categoryTags - Tags de categories possibles
   * @returns {string|null} Categorie trouvee
   */
  getProductCategory(product, categoryTags) {
    // 1. Recherche prioritaire par tags exacts
    for (const category of categoryTags) {
      if (product.tags.includes(category)) {
        return category;
      }
    }

    // 2. Fallback intelligent par mots-clés dans le titre ou le type
    const title = (product.title || '').toLowerCase();
    const type = (product.productType || '').toLowerCase();

    // Mapping des synonymes pour le fallback
    if (title.includes('shampoing') || title.includes('shampoo') || type.includes('shampoo')) return 'shampoing';
    if (title.includes('après-shampoing') || title.includes('conditioner') || title.includes('apres-shampoing')) return 'apres-shampoing';
    if (title.includes('masque') || title.includes('mask')) return 'masque';
    if (title.includes('sérum') || title.includes('serum')) return 'serum';
    if (title.includes('huile') || title.includes('oil')) return 'huile-cheveux';
    if (title.includes('nettoyant') || title.includes('cleanser')) return 'nettoyant';
    if (title.includes('hydratant') || title.includes('moisturizer') || title.includes('cream') || title.includes('crème')) return 'hydratant';

    return null;
  }
}

/**
 * Classe utilitaire pour gerer l'ajout de plusieurs produits au panier
 */
class GlowseraCartManager {
  /**
   * Ajouter plusieurs produits au panier
   * @param {Array<Object>} products - Liste de produits a ajouter
   * @returns {Promise<Object>} Resultat
   */
  static async addMultipleToCart(products) {
    try {
      console.log('[Glowsera Cart Manager] Ajout de', products.length, 'produits au panier');

      const results = {
        success: [],
        errors: []
      };

      // Ajouter chaque produit sequentiellement
      for (const product of products) {
        try {
          const variantId = product.variants[0].id; // Prendre la premiere variante
          await addToCart(variantId, 1);
          results.success.push(product);

          // Tracker l'ajout
          if (typeof gtag !== 'undefined') {
            gtag('event', 'add_to_cart_from_quiz', {
              product_id: product.id,
              product_name: product.title,
              value: product.price,
              currency: product.currency,
              quiz_type: product.quizType || 'unknown'
            });
          }

        } catch (error) {
          console.error('[Glowsera Cart Manager] Erreur ajout produit:', product.title, error);
          results.errors.push({
            product: product,
            error: error.message
          });
        }
      }

      console.log('[Glowsera Cart Manager] Resultats:', results);

      return results;

    } catch (error) {
      console.error('[Glowsera Cart Manager] Erreur ajout multiple:', error);
      throw error;
    }
  }
}

// Export global
window.GlowseraRecommendationEngine = GlowseraRecommendationEngine;
window.GlowseraCartManager = GlowseraCartManager;
window.glowseraQuizAPI = {
  fetchProductsByTags,
  addToCart,
  ProductCache
};

console.log('[Glowsera Quiz API] Module charge avec succes');
